import _ from 'underscore';

function castPath(value, object) {
  if (_.isArray(value)) {
    return value;
  }

  if (_.has(object, value)) {
    return [value];
  }

  return _.compact(value.split(/[[\].]/));
}

function clone(item, sourceCopyMap) {
  if (!_.isObject(item)) {
    return item;
  }
  if (!sourceCopyMap.get(item)) {
    const result = _.isArray(item) ? [] : {};
    sourceCopyMap.set(item, result);
    _.each(item, (value, key) => {
      result[key] = clone(value, sourceCopyMap);
    });
  }
  return sourceCopyMap.get(item);
}

/**
 * defined in lodash 4.5.1
 * sum list with iteratee
 * @param {Object[]} list - array to be summed
 * @param {Function} invoke - function invoked per element
 * @returns {any} - sum value of list
 */
export function sumBy(list, invoke) {
  const iteratee = _.iteratee(invoke);
  return _.reduce(
    list,
    (currentSum, item, index, arr) => currentSum + iteratee(item, index, arr),
    0);
}

/**
 * defined in lodash 0.5.0
 * merge two object from src to dest
 * @param {Object} dest - dest object
 * @param {Object} src - src object
 * @returns {Object} - merged object
 */
export function merge(...args) {
  return _.reduce(_.rest(args), (dest, src) => {
    _.each(src, (value, key) => {
      if (_.isObject(dest[key]) && _.isObject(value)) {
        merge(dest[key], value);
      } else {
        _.extend(dest, _.object([key], [value]));
      }
    });

    return dest;
  }, _.first(args) || {});
}

/**
 * defined in lodash 4.6.1
 * Gets the value at path of object. If the resolved value is undefined,
 * the defaultValue is returned in its place.
 * @param {Object} object - The object to query.
 * @param {array|string} path - The path of the property to get.
 * @param {*} defaultValue - Optional, The value returned for undefined resolved values.
 * @returns {any} - return the value in object by path, if not exist, return default value
 */
export function get(object, path, defaultValue) {
  let sub;
  let i;
  const keyPath = castPath(path, object);

  for (i = 0, sub = object; i < keyPath.length; i += 1) {
    if (!_.isObject(sub)) {
      return defaultValue;
    }

    sub = sub[keyPath[i]];

    if (_.isUndefined(sub)) {
      return defaultValue;
    }
  }

  return sub;
}

/**
 * lodash set, path don't support index
 * Sets the value at path of object. If a portion of path doesn't exist, it's created.
 * Arrays are created for missing index properties while
 * objects are created for all other missing properties.
 * @param {Object} object - The object to modify.
 * @param {string} path - The path of the property to set.
 * @param {*} value - The value to set.
 * @returns {Object} - Returns object.
 */
export function set(object, path, value) {
  if (!_.isObject(object)) {
    return object;
  }

  const keyPath = castPath(path, object);
  let index = 0;
  const length = keyPath.length;
  const lastIndex = length - 1;
  let nested = object;

  while (nested != null && index < length) {
    const key = keyPath[index];

    if (index !== lastIndex) {
      nested[key] = nested[key] || {};
      nested = nested[key];
    } else {
      nested[key] = value;
    }
    index += 1;
  }
  return object;
}

/**
 * Creates a function that returns the result of invoking the given
 * functions with the this binding of the created function,
 * where each successive invocation is supplied the return value of the previous.
 * lodash flow https://lodash.com/docs#flow
 * @param {function} funcs - The functions to invoke.
 * @returns {function} - Returns the new composite function.
 */
export function flow(...funcs) {
  return _.compose.apply(null, funcs.reverse());
}

/**
 * defined in lodash 4.0.0
 * Check whether value is undefined or null
 * @param {*} value - value to Check
 * @returns {boolean} - return true if value is undefined or null, otherwise, return false
 */
export function isNil(value) {
  return _.isUndefined(value) || _.isNull(value);
}

/**
 * deep clone object
 * @param {array|Object} item - source item to be copied
 * @return {array|Object} - copied from item
*/
export function cloneDeep(item) {
  return clone(item, new Map());
}

/**
 * array dedup function
 * @param {any[]} array - the array to inspect
 * @param {function} comparator - comparator function to determine whether 2 elements are the same
 * @returns {array} - new duplicate free array
 */
export function uniqWith(array, comparator) {
  if (!_.isArray(array) || !array.length) {
    return [];
  }
  if (!_.isFunction(comparator)) {
    return _.uniq(array);
  }
  const result = [];
  _.each(array, (obj) => {
    if (_.every(result, res => !comparator(obj, res))) {
      result.push(obj);
    }
  });
  return result;
}



// WEBPACK FOOTER //
// ./d:/dbs/el/aapusc/private/client/packages/primitive-utilities/src/lodash.es